import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  Box, Flex, HStack, VStack, Text, Button, IconButton, Image, Textarea, Link, Spinner, Tooltip,
  useBreakpointValue, usePrefersReducedMotion,
} from '@chakra-ui/react';
import { useAuth } from '../auth/AuthContext';
import { useDocs } from '../docs/DocsContext';
import { supabase } from '../lib/supabase';
import { headingId } from '../lib/slug';
import { askLay, escalateLay, escalationState, demoAnswer, scopeFromAudiences, MAX_MESSAGE } from '../lib/layChat';

// 레이(Lay) 위젯 — 우측 하단 런처 + 패널. 기획: docs/ops/docs-chatbot-plan.md §4
//
// 켜는 조건: REACT_APP_LAY_WIDGET=1. 서버 주소는 REACT_APP_LAY_CHAT_URL.
// 프리뷰 빌드(REACT_APP_PREVIEW=1)에서는 서버 대신 lib/layChat.demoAnswer 가 답한다.
// 「운영팀에 물어볼까요?」(v11, 2026-09-10 · 기획 lay-ai-operations-plan.md §3-5 (나)): 가이드에 없는 답이면 서버가 바로 넘기지 않고
// escalation:'offer' 로 돌려준다. 여기서 예/아니요를 묻고 「예」일 때만 escalateLay() 로 넣는다. 「아니요」는 서버에 보내지 않는다.
// 디자인은 시안 C(도구)로 확정 (2026-09-01). 시안 A·B 캡처는 docs/design/lay-widget/ 에 남아 있다.

const CHAT_URL = process.env.REACT_APP_LAY_CHAT_URL || '';
const KAKAO_URL = process.env.REACT_APP_LAY_KAKAO_URL || '';
const CONTACT_URL = process.env.REACT_APP_LAY_CONTACT_URL || '';
const STORE_KEY = 'lay-widget:v1';
const RATE_LOCK_SECONDS = 60;

const SUGGESTIONS = {
  public: ['가입 승인은 어떻게 받나요?', '로그인이 안 돼요', '약관에서 삭제 요청 조항이 뭔가요?', '개인정보는 어디에 쓰이나요?'],
  creator: ['채널 등록은 어떻게 하나요?', '정산은 언제 신청할 수 있나요?', '식별코드가 뭔가요?', '등급은 언제 정해지나요?'],
  rights: ['검수 반려 사유는 어떻게 고르나요?', '정산내역서는 어디서 보나요?', '협업 채널은 어떻게 고르나요?', '작품 제공 조건은 어떻게 정하나요?'],
};
SUGGESTIONS.all = SUGGESTIONS.creator;

// ── 스킨 (시안 C · 도구) ────────────────────────────────────────────────────
const SKIN = {
  // 각지고 조밀하게. 말풍선 대신 문서 발췌처럼 보여 "가이드에서 찾아 준다" 는 성격을 드러낸다.
  launcher: { shape: 'pill', size: 44, logo: 22, text: 'AI 레이에게 묻기',
    box: { bg: 'neutral.900', color: 'white', borderRadius: '8px', boxShadow: 'lg', _hover: { bg: 'neutral.800' } } },
  panel: { bg: 'white', borderWidth: '1px', borderColor: 'neutral.300', borderRadius: '6px', boxShadow: '0 12px 40px rgba(26,25,24,0.18)' },
  header: { box: { bg: 'neutral.900' }, title: 'white', sub: 'neutral.400', icon: 'neutral.300', logo: 22, logoWrap: {} },
  band: { bg: 'accent.50', color: 'accent.800', borderBottomWidth: '1px', borderColor: 'accent.100' },
  body: { bg: 'white' },
  messages: 'lines',
  user: { color: 'neutral.800' },
  lay: { color: 'neutral.800', borderLeftWidth: '2px', borderColor: 'brand.500', pl: 3 },
  chip: { bg: 'transparent', color: 'neutral.700', borderRadius: '4px', px: 0, _hover: { color: 'brand.700', bg: 'neutral.50' }, arrow: true },
  source: { bg: 'transparent', color: 'brand.700', borderRadius: '0', px: 0, textDecoration: 'underline', textUnderlineOffset: '3px', _hover: { color: 'brand.800' } },
  escalate: { bg: 'neutral.50', borderWidth: '1px', borderColor: 'neutral.200', borderRadius: '4px', color: 'neutral.700' },
  footer: { bg: 'neutral.50', borderTopWidth: '1px', borderColor: 'neutral.200' },
  input: { borderRadius: '4px', borderColor: 'neutral.300', focusBorderColor: 'neutral.800', bg: 'white' },
  send: { bg: 'neutral.900', color: 'white', borderRadius: '4px', _hover: { bg: 'neutral.800' } },
};

function readStore() {
  try { return JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null'); } catch (e) { return null; }
}
function writeStore(v) {
  try { sessionStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch (e) { /* noop */ }
}

const mdComponents = {
  p: (p) => <Text fontSize="13.5px" lineHeight="1.65" mb={1.5} sx={{ '&:last-child': { mb: 0 } }} {...p} />,
  ol: (p) => <Box as="ol" pl={5} mb={1.5} fontSize="13.5px" lineHeight="1.65" {...p} />,
  ul: (p) => <Box as="ul" pl={5} mb={1.5} fontSize="13.5px" lineHeight="1.65" {...p} />,
  li: (p) => <Box as="li" mb={0.5} {...p} />,
  strong: (p) => <Text as="strong" fontWeight="600" {...p} />,
  a: (p) => <Link color="brand.700" textDecoration="underline" textUnderlineOffset="2px" isExternal {...p} />,
  code: (p) => <Text as="code" fontSize="0.9em" bg="blackAlpha.100" px={1} borderRadius="3px" {...p} />,
};

// ── AI · 베타 표시 (2026-09-06 대표 확정: 시안 A 스파클+AI 라벨 + 베타 2안 모서리 사선 띠) ──
// docs/design/lay-launcher-ai-variants.html. AI 표시는 사람 상담원으로 오해하지 않게, 베타 표시는 답이
// 부족해도 양해를 구하기 위해 붙인다. 휴대폰(글자 숨김·로고만)에서는 띠가 잘리므로 칩으로 대체한다.

// 스파클 ✦ — AI 기능의 관용 기호. 큰 별은 브랜드 청록, 작은 별은 보라(이 아이콘에만 쓴다).
function SparkIcon({ size = 14 }) {
  return (
    <Box as="svg" viewBox="0 0 24 24" w={`${size}px`} h={`${size}px`} flexShrink={0} aria-hidden="true">
      <path d="M12 2l1.9 6.1L20 10l-6.1 1.9L12 18l-1.9-6.1L4 10l6.1-1.9L12 2z" fill="#3cb7a0" />
      <path d="M19 15l.9 2.6 2.6.9-2.6.9-.9 2.6-.9-2.6-2.6-.9 2.6-.9L19 15z" fill="#7c6cf2" />
    </Box>
  );
}

// 'AI' 글자 칩 — 청록 테두리, 모노스페이스.
function AiTag() {
  return (
    <Text as="span" fontFamily="mono" fontSize="10px" fontWeight="600" letterSpacing="0.08em" lineHeight="1.3" px={1.5}
      borderRadius="4px" color="brand.400" borderWidth="1px" borderColor="rgba(60,183,160,0.55)" flexShrink={0} aria-label="AI 도우미">
      AI
    </Text>
  );
}

// 베타 칩 — 휴대폰 런처용(띠를 그릴 자리가 없다).
function BetaChip({ display }) {
  return (
    <Text as="span" display={display} fontSize="9.5px" fontWeight="700" letterSpacing="0.08em" lineHeight="1" px={1.5} py={0.5}
      borderRadius="3px" bg="accent.400" color="neutral.900" flexShrink={0} aria-label="베타 서비스">
      BETA
    </Text>
  );
}

// 베타 사선 띠 — 부모(position relative · overflow hidden)의 오른쪽 위 모서리에 38° 로 걸친다.
function BetaRibbon({ display, top = '7px', right = '-26px' }) {
  return (
    <Box as="span" display={display} position="absolute" top={top} right={right} transform="rotate(38deg)" transformOrigin="center"
      bg="accent.400" color="neutral.900" fontFamily="mono" fontSize="8.5px" fontWeight="700" letterSpacing="0.12em" lineHeight="1.3"
      px="30px" py="2px" boxShadow="0 1px 0 rgba(0,0,0,0.25)" pointerEvents="none" aria-label="베타 서비스" whiteSpace="nowrap">
      BETA
    </Box>
  );
}

function Logo({ size }) {
  return <Image src={`${process.env.PUBLIC_URL}/laeebly-icon.png`} alt="" w={`${size}px`} h={`${size}px`} draggable={false} />;
}

function Sources({ skin, sources, onOpen }) {
  if (!sources?.length) return null;
  const lines = skin.messages === 'lines';
  return (
    <Flex mt={2} gap={lines ? 0 : 1.5} wrap="wrap" direction={lines ? 'column' : 'row'} align="flex-start">
      {lines && <Text fontSize="10.5px" fontWeight="600" letterSpacing="0.08em" color="neutral.500" mb={0.5}>출처</Text>}
      {sources.map((s, i) => (
        <Button key={`${s.slug}-${i}`} size="xs" h="auto" py={1} px={2.5} fontWeight="500" fontSize="11.5px" whiteSpace="normal"
          textAlign="left" onClick={() => onOpen(s)} {...skin.source}>
          {s.title}{s.heading ? ` › ${s.heading}` : ''}
        </Button>
      ))}
    </Flex>
  );
}

function ContactButtons() {
  if (!KAKAO_URL && !CONTACT_URL) return null;
  return (
    <HStack spacing={1.5} mt={2}>
      {KAKAO_URL && <Button as="a" href={KAKAO_URL} target="_blank" rel="noopener noreferrer" size="xs" variant="outline" borderColor="neutral.300">카카오톡 레이</Button>}
      {CONTACT_URL && <Button as="a" href={CONTACT_URL} target="_blank" rel="noopener noreferrer" size="xs" variant="outline" borderColor="neutral.300">고객센터</Button>}
    </HStack>
  );
}

// 「운영팀에 물어볼까요?」 — offer(되묻기) → sent(전달됨) | declined(가이드·문의 안내). 기획 §3-5 (나).
function Escalation({ skin, m, signedIn, onDecide }) {
  const state = escalationState(m);
  if (state === 'none') return null;
  const box = { mt: 2, px: 3, py: 2.5, fontSize: '12.5px', lineHeight: '1.6', ...skin.escalate };
  if (state === 'offer') {
    return (
      <Box {...box} role="group" aria-label="이건 제가 확실히 몰라요. 운영팀에 물어볼까요?">
        <Text>이건 제가 확실히 몰라요. 운영팀에 물어볼까요?</Text>
        {m.escalating ? (
          <HStack spacing={2} mt={2} color="neutral.500"><Spinner size="xs" color="brand.500" /><Text>전달 중…</Text></HStack>
        ) : (
          <HStack spacing={1.5} mt={2}>
            <Button size="xs" bg="neutral.900" color="white" _hover={{ bg: 'neutral.800' }} onClick={() => onDecide('yes')}>네, 물어봐 주세요</Button>
            <Button size="xs" variant="outline" borderColor="neutral.300" onClick={() => onDecide('no')}>아니요, 괜찮아요</Button>
          </HStack>
        )}
        {m.escalateError && <Text mt={1.5} color="status.error" role="alert">{m.escalateError}</Text>}
      </Box>
    );
  }
  if (state === 'declined') {
    return (
      <Box {...box}>
        <Text>알겠어요. 가이드에서 더 찾아보시거나, 아래로 물어보셔도 돼요.</Text>
        <ContactButtons />
      </Box>
    );
  }
  return (
    <Box {...box}>
      <Text>
        {signedIn
          ? '운영팀에 전달됐어요. 답이 오면 레이블리 앱의 「내 대화 기록」에 담당자 확인 답변으로 붙어요.'
          : '운영팀에 전달됐어요. 로그인한 상태로 물으면 답을 레이블리 앱의 「내 대화 기록」에서 받아볼 수 있어요.'}
      </Text>
      <ContactButtons />
    </Box>
  );
}

function Message({ skin, m, signedIn, onOpenSource, onDecide }) {
  const isUser = m.role === 'user';
  if (skin.messages === 'lines') {
    return (
      <Box>
        <Text fontSize="10.5px" fontWeight="600" letterSpacing="0.08em" color={isUser ? 'neutral.400' : 'brand.600'} mb={1}>
          {isUser ? '나' : '레이'}{m.demo && !isUser ? ' · 미리보기 응답' : ''}
        </Text>
        <Box {...(isUser ? skin.user : skin.lay)} fontSize="13.5px" lineHeight="1.65">
          {isUser ? <Text whiteSpace="pre-wrap">{m.text}</Text> : <ReactMarkdown components={mdComponents}>{m.text}</ReactMarkdown>}
          {!isUser && <Sources skin={skin} sources={m.sources} onOpen={onOpenSource} />}
          {!isUser && <Escalation skin={skin} m={m} signedIn={signedIn} onDecide={onDecide} />}
        </Box>
      </Box>
    );
  }
  return (
    <Flex direction="column" align={isUser ? 'flex-end' : 'flex-start'}>
      <Box maxW="88%" px={3.5} py={2.5} fontSize="13.5px" lineHeight="1.65" {...(isUser ? skin.user : skin.lay)}>
        {isUser ? <Text whiteSpace="pre-wrap">{m.text}</Text> : <ReactMarkdown components={mdComponents}>{m.text}</ReactMarkdown>}
        {!isUser && <Sources skin={skin} sources={m.sources} onOpen={onOpenSource} />}
        {!isUser && <Escalation skin={skin} m={m} signedIn={signedIn} onDecide={onDecide} />}
      </Box>
      {!isUser && m.demo && <Text fontSize="10.5px" color="neutral.400" mt={1}>미리보기 응답 — 문서에서 문단을 찾아 보여 줍니다</Text>}
    </Flex>
  );
}

function Suggestions({ skin, items, onPick }) {
  const { arrow, ...chip } = skin.chip;
  return (
    <Box>
      <Text fontSize="11px" fontWeight="600" letterSpacing="0.08em" color="neutral.500" mb={2}>이런 걸 물어보세요</Text>
      <VStack align={arrow ? 'stretch' : 'flex-start'} spacing={1.5}>
        {items.map((q) => (
          <Button key={q} size="sm" h="auto" py={2} px={3} fontWeight="500" fontSize="13px" whiteSpace="normal" textAlign="left"
            onClick={() => onPick(q)} rightIcon={arrow ? <Text as="span" color="neutral.400">→</Text> : undefined} {...chip}>
            {q}
          </Button>
        ))}
      </VStack>
    </Box>
  );
}

function LayWidgetInner() {
  const { signedIn, isPreview } = useAuth();
  const { audiences, visiblePages: pages, bySlug } = useDocs();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const reduceMotion = usePrefersReducedMotion();

  const scope = scopeFromAudiences(audiences);
  const currentSlug = bySlug[location.pathname.slice(1)] ? location.pathname.slice(1) : null;

  const skin = SKIN;

  const [open, setOpen] = useState(false);
  // 되묻기 전달 중(escalating) 표시는 진행 상태라 저장본에서는 뺀다 — 새로 고침 뒤 스피너가 영원히 돌지 않게.
  const [messages, setMessages] = useState(() => { const s = readStore(); return s && s.scope === scope ? (s.messages || []).map(({ escalating, ...m }) => m) : []; });
  const [conversationId, setConversationId] = useState(() => { const s = readStore(); return s && s.scope === scope ? s.conversationId || null : null; });
  const [input, setInput] = useState('');
  const [hp, setHp] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [lockedUntil, setLockedUntil] = useState(0);
  const [now, setNow] = useState(Date.now());

  const launcherRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const scopeRef = useRef(scope);

  // 역할이 바뀌면(로그인·로그아웃) 대화를 끊는다 — 범위가 다른 답이 한 대화에 섞이지 않게.
  useEffect(() => {
    if (scopeRef.current === scope) return;
    scopeRef.current = scope;
    setMessages([]); setConversationId(null); setError(null);
  }, [scope]);

  useEffect(() => { writeStore({ scope, messages, conversationId }); }, [scope, messages, conversationId]);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => inputRef.current?.focus(), 60);
    const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); } };
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); };
  }, [open]);

  const close = useCallback(() => { setOpen(false); setTimeout(() => launcherRef.current?.focus(), 0); }, []);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending, open]);

  // 429 잠금 카운트다운
  useEffect(() => {
    if (lockedUntil <= Date.now()) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [lockedUntil]);
  const lockedSeconds = Math.max(0, Math.ceil((lockedUntil - now) / 1000));

  const send = useCallback(async (raw) => {
    const message = String(raw || '').trim().slice(0, MAX_MESSAGE);
    if (!message || sending || lockedSeconds > 0) return;
    setError(null);
    setInput('');
    setMessages((m) => [...m, { role: 'user', text: message }]);
    setSending(true);
    try {
      let r;
      if (isPreview) {
        await new Promise((res) => setTimeout(res, 450));
        r = demoAnswer(message, pages, (typeof window !== 'undefined' && window.__DOCS__) || {}, currentSlug);
      } else {
        let accessToken = null;
        try { const { data } = await supabase.auth.getSession(); accessToken = data?.session?.access_token || null; } catch (e) { accessToken = null; }
        r = await askLay({ url: CHAT_URL, message, conversationId, currentSlug, accessToken, honeypot: hp });
        if (r.conversationId) setConversationId(r.conversationId);
      }
      // 메시지에 대화 id·메시지 id 를 같이 둔다 — 「새 대화」로 conversationId 가 바뀐 뒤에도 이 답의 되묻기가 제 대화에 붙게.
      setMessages((m) => [...m, { role: 'lay', text: r.content, sources: r.sources, escalated: r.escalated, escalation: r.escalation, conversationId: r.conversationId || null, messageId: r.messageId || null, demo: !!r.demo }]);
    } catch (e) {
      if (e?.status === 429) setLockedUntil(Date.now() + (e.retryAfter || RATE_LOCK_SECONDS) * 1000);
      setError(e?.content || '지금 답변 생성이 어려워요. 잠시 후 다시 시도해 주세요.');
    } finally {
      setSending(false);
    }
  }, [sending, lockedSeconds, isPreview, pages, currentSlug, conversationId, hp]);

  // 「운영팀에 물어볼까요?」 예/아니요. 「아니요」는 서버에 보내지 않는다(기획 §3-5 (나) — 슬랙 미발송, 안내로 종료). 프리뷰는 화면만 바꾼다.
  const decide = useCallback(async (idx, decision) => {
    const patch = (p) => setMessages((ms) => ms.map((x, i) => (i === idx ? { ...x, ...p } : x)));
    const m = messages[idx];
    if (!m || escalationState(m) !== 'offer' || m.escalating) return;
    if (decision === 'no') { patch({ escalation: 'declined', escalateError: null }); return; }
    if (isPreview || m.demo) { patch({ escalation: 'sent' }); return; }
    patch({ escalating: true, escalateError: null });
    try {
      let accessToken = null;
      try { const { data } = await supabase.auth.getSession(); accessToken = data?.session?.access_token || null; } catch (e) { accessToken = null; }
      await escalateLay({ url: CHAT_URL, conversationId: m.conversationId || conversationId, messageId: m.messageId, accessToken });
      patch({ escalation: 'sent', escalating: false });
    } catch (e) {
      patch({ escalating: false, escalateError: e?.content || '전달에 실패했어요. 다시 시도해 주세요.' });
    }
  }, [messages, conversationId, isPreview]);

  const reset = () => { setMessages([]); setConversationId(null); setError(null); setInput(''); inputRef.current?.focus(); };

  const openSource = (s) => {
    navigate(`/${s.slug}`);
    if (s.heading) setTimeout(() => document.getElementById(headingId(s.heading))?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' }), 120);
    if (isMobile) setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(input); }
  };

  const L = skin.launcher;
  const transition = reduceMotion ? undefined : 'opacity 160ms ease, transform 160ms ease';

  return (
    <>
      {!(open && isMobile) && (
        <Box position="fixed" right={{ base: '16px', md: '20px' }} bottom={{ base: '16px', md: '20px' }} zIndex={1200}>
          <Tooltip label={L.text} placement="left" hasArrow isDisabled={L.shape === 'pill'} openDelay={300}>
            {/* 띠를 모서리에서 잘라내기 위한 래퍼 — 그림자는 래퍼가, 버튼은 overflow 안에서 그린다 */}
            <Box position="relative" display="inline-block" overflow="hidden" borderRadius={L.shape === 'pill' ? L.box.borderRadius : '50%'}
              boxShadow={L.box.boxShadow} transition={transition}>
              <Button ref={launcherRef} aria-label={`${L.text} (AI 도우미 · 베타)`} aria-expanded={open} aria-controls="lay-widget-panel"
                onClick={() => setOpen((o) => !o)} h={`${L.size}px`} minW={`${L.size}px`}
                pl={L.shape === 'pill' ? 3 : 0} pr={L.shape === 'pill' ? { base: 3, md: '34px' } : 0}
                borderRadius={L.shape === 'pill' ? L.box.borderRadius : '50%'}
                display="inline-flex" alignItems="center" gap={2} {...L.box} boxShadow="none">
                {L.ring ? (
                  <Box bg="white" borderRadius="50%" w={`${L.logo + 6}px`} h={`${L.logo + 6}px`} display="grid" placeItems="center"><Logo size={L.logo} /></Box>
                ) : <Logo size={L.logo} />}
                <SparkIcon />
                {L.shape === 'pill' && <Text fontSize="13.5px" fontWeight="600" display={{ base: 'none', md: 'block' }}>{L.text}</Text>}
                <Box display={{ base: 'none', md: 'block' }}><AiTag /></Box>
                <BetaChip display={{ base: 'inline-block', md: 'none' }} />
              </Button>
              <BetaRibbon display={{ base: 'none', md: 'block' }} />
            </Box>
          </Tooltip>
        </Box>
      )}

      {open && (
        <Flex id="lay-widget-panel" role="dialog" aria-label="레이 이용 가이드 도우미" direction="column"
          position="fixed" zIndex={1200} overflow="hidden"
          right={{ base: 0, md: '20px' }} bottom={{ base: 0, md: `${L.size + 32}px` }} left={{ base: 0, md: 'auto' }} top={{ base: 0, md: 'auto' }}
          w={{ base: '100%', md: '380px' }} h={{ base: '100dvh', md: 'min(600px, calc(100dvh - 96px))' }}
          {...skin.panel} borderRadius={{ base: 0, md: skin.panel.borderRadius }} transition={transition}
          sx={{ '@media (prefers-reduced-motion: no-preference)': { animation: 'lay-in 160ms ease' }, '@keyframes lay-in': { from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1, transform: 'none' } } }}>

          <Flex align="center" gap={2.5} px={3.5} py={3} pr="56px" {...skin.header.box} flexShrink={0} position="relative" overflow="hidden">
            <BetaRibbon top="8px" right="-28px" />
            <Box {...skin.header.logoWrap} flexShrink={0}><Logo size={skin.header.logo} /></Box>
            <Box minW={0}>
              <HStack spacing={1.5} align="center">
                <Text fontSize="14px" fontWeight="700" color={skin.header.title} lineHeight="1.2">레이 · AI 이용 가이드 도우미</Text>
                <AiTag />
              </HStack>
              <Text fontSize="11.5px" color={skin.header.sub} mt={0.5}>베타예요 — 가이드 원문을 근거로 AI 가 답하고, 부족하거나 틀릴 수 있어요</Text>
            </Box>
            <Box flex="1" />
            {messages.length > 0 && (
              <Tooltip label="새 대화" hasArrow openDelay={300}>
                <IconButton aria-label="새 대화" size="sm" variant="ghost" color={skin.header.icon} _hover={{ bg: 'blackAlpha.100' }} onClick={reset}
                  icon={<Text fontSize="16px" lineHeight="1">↺</Text>} />
              </Tooltip>
            )}
            <IconButton aria-label="닫기" size="sm" variant="ghost" color={skin.header.icon} _hover={{ bg: 'blackAlpha.100' }} onClick={close}
              icon={<Text fontSize="18px" lineHeight="1">×</Text>} />
          </Flex>

          {!signedIn && (
            <Flex align="center" gap={2} px={3.5} py={2} fontSize="12px" {...skin.band} flexShrink={0}>
              <Text flex="1">로그인하면 <b>서비스 이용 문서</b>까지 답해드려요</Text>
              <Button size="xs" variant="link" color="inherit" fontWeight="700" textDecoration="underline" textUnderlineOffset="2px"
                onClick={() => { setOpen(false); navigate(`/login?next=${encodeURIComponent(location.pathname)}`); }}>로그인</Button>
            </Flex>
          )}

          <Box ref={listRef} flex="1" minH={0} overflowY="auto" overscrollBehavior="contain" px={3.5} py={3.5} {...skin.body}>
            {messages.length === 0 ? (
              <Suggestions skin={skin} items={SUGGESTIONS[scope] || SUGGESTIONS.public} onPick={(q) => send(q)} />
            ) : (
              <VStack align="stretch" spacing={3}>
                {messages.map((m, i) => <Message key={i} skin={skin} m={m} signedIn={signedIn} onOpenSource={openSource} onDecide={(d) => decide(i, d)} />)}
                {sending && (
                  <HStack spacing={2} color="neutral.500" fontSize="12.5px"><Spinner size="xs" color="brand.500" /><Text>가이드에서 찾는 중…</Text></HStack>
                )}
                {error && <Text fontSize="12.5px" color="status.error" role="alert">{error}</Text>}
              </VStack>
            )}
          </Box>

          <Box as="form" onSubmit={(e) => { e.preventDefault(); send(input); }} px={3} pt={2.5} pb={2} {...skin.footer} flexShrink={0}>
            {/* 허니팟 — 봇이 채우는 숨은 필드. 서버가 값이 있으면 조용히 버린다. */}
            <input type="text" name="_hp" value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }} />
            <Flex gap={2} align="flex-end">
              <Textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value.slice(0, MAX_MESSAGE))} onKeyDown={onKeyDown}
                placeholder={lockedSeconds > 0 ? `${lockedSeconds}초 뒤에 다시 질문할 수 있어요` : '질문 입력…'}
                rows={1} resize="none" minH="40px" maxH="120px" fontSize="13.5px" py={2} isDisabled={sending || lockedSeconds > 0}
                aria-label="질문" {...skin.input} />
              <Button type="submit" h="40px" px={3.5} fontSize="13px" fontWeight="600" isDisabled={!input.trim() || sending || lockedSeconds > 0} {...skin.send}>보내기</Button>
            </Flex>
            <Flex justify="space-between" mt={1.5} fontSize="10.5px" color="neutral.400">
              <Text>베타 · 답변은 가이드 문서를 근거로 생성되며 틀릴 수 있어요. 중요한 내용은 출처 문서에서 확인해 주세요</Text>
              {input.length > MAX_MESSAGE - 100 && <Text>{input.length}/{MAX_MESSAGE}</Text>}
            </Flex>
          </Box>
        </Flex>
      )}
    </>
  );
}

export default function LayWidget() {
  if (process.env.REACT_APP_LAY_WIDGET !== '1') return null;
  return <LayWidgetInner />;
}
